import { Injectable } from '@angular/core';
import * as SentryAngular from '@sentry/angular';
import * as Sentry from '@sentry/capacitor';
import { CaptureContext, Extra, Extras, Log, ParameterizedString, Span, StartSpanOptions, User } from '@sentry/core';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SentryWrapper {

  setUser(user: User | null): void {
    Sentry.setUser(user);
  }

  captureException(exception: any, captureContext?: CaptureContext): string {
    return Sentry.captureException(exception, captureContext);
  }

  captureMessage(message: string, captureContext?: CaptureContext | Sentry.SeverityLevel): string {
    return Sentry.captureMessage(message, captureContext);
  }

  addBreadcrumb(breadcrumb: Sentry.Breadcrumb): void {
    Sentry.addBreadcrumb(breadcrumb);
  }

  setContext(name: string, context: { [key: string]: any } | null): void {
    Sentry.setContext(name, context);
  }

  setExtras(extras: Extras): void {
    Sentry.setExtras(extras);
  }

  setExtra(key: string, extra: Extra): void {
    Sentry.setExtra(key, extra);
  }

  setTag(key: string, value: string): void {
    Sentry.setTag(key, value);
  }

  setGlobalAttributes(attributes: Record<string, any>): void {
    Sentry.getGlobalScope().setAttributes(attributes);
  }

  withinSpan<T>(options: StartSpanOptions, callback: (span: Span) => T): T {
    const span = Sentry.startSpan(options, callback);
    return span;
  }

  debug(message: ParameterizedString, attributes?: Log['attributes']): void {
    Sentry.logger.debug(message, attributes);
  }

  info(message: ParameterizedString, attributes?: Log['attributes']): void {
    Sentry.logger.info(message, attributes);
  }

  warn(message: ParameterizedString, attributes?: Log['attributes']): void {
    Sentry.logger.warn(message, attributes);
  }

  error(message: ParameterizedString, attributes?: Log['attributes']): void {
    Sentry.logger.error(message, attributes);
  }

  fatal(message: ParameterizedString, attributes?: Log['attributes']): void {
    Sentry.logger.fatal(message, attributes);
  }

  async flushReplay(): Promise<void> {
    const replay = SentryAngular.getReplay();
    await replay.flush();
  }
}

export function getSentryConfigurationJson(): string | null {
  const sentryOptions = Sentry.getClient()?.getOptions()
  if (!sentryOptions) {
    return null;
  }

  const logAttributes = Sentry.getGlobalScope().getScopeData().attributes
  const sentryOptionsObj = {
    dsn: sentryOptions.dsn,
    release: sentryOptions.release,
    dist: sentryOptions.dist,
    environment: sentryOptions.environment,
    enabled: sentryOptions.enabled,
    enableLogs: sentryOptions.enableLogs,
    tracesSampleRate: sentryOptions.tracesSampleRate,
    logAttributes: { ...logAttributes }
  }

  return JSON.stringify(sentryOptionsObj)
}

//eslint-disable-next-line max-lines-per-function
export function initSentry(): void {
  Sentry.init(
    {
        dsn: 'https://936dcdcbd5a1055563a902ce6c39e489@sentry.rsginc.com/2',
      ignoreErrors: [
        '"TextZoom" plugin is not implemented on web',
      ],
      release: environment.browserAppConfig.version,
      dist: environment.browserAppConfig.distributionVersion,
      environment: environment.name,
      enabled: process.env.ENABLE_SENTRY === 'true',
      enableLogs: true,
      replaysSessionSampleRate: 0.01,
      replaysOnErrorSampleRate: 1.0,
      tracesSampleRate: 1.0,
      integrations: [
        SentryAngular.captureConsoleIntegration({
          levels: ['error']
        }),
        SentryAngular.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
        SentryAngular.browserTracingIntegration(),
      ],
      // tracePropagationTargets: ['localhost', environment.serverUrl],
    },
    SentryAngular.init
  );
}
